import { ArrowDown, ArrowUp } from "lucide-react";
import { formatPaymentDate } from "@/utils/format-date";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";
import { ApiDataProps } from "@/pages/Tenants";
import { CashFlowItemProps } from "@/types/cashFlow";
import { FINANCIAL_TRANSACTION_CONST } from "@/constants/FinancialTransactionTypeConst";

type TableDataProps = {
  dataApi: ApiDataProps<CashFlowItemProps>;
  onPageChange: (page: number) => void;
};

export const TableData = ({ dataApi, onPageChange }: TableDataProps) => (
  <>
    <div className="rounded-md border bg-white dark:bg-gray-800 mb-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tipo</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead>Valor</TableHead>
            <TableHead>Data de Pagamento</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi.data.length > 0 ? (
            dataApi.data.map((item) => {
              const isPayout =
                item.financial_transaction_type_id ===
                FINANCIAL_TRANSACTION_CONST.ACCOUNT_PAYABLE;
              const category = isPayout
                ? item.expense_type?.category?.name
                : item.revenue_type?.category?.name;
              const isReconciled = item.reconciled_amount !== null;
              const value = isReconciled
                ? Number(item.reconciled_amount)
                : item.amount;

              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <div
                      className={cn(
                        "flex items-center gap-1",
                        isPayout ? "text-red-600" : "text-green-600"
                      )}
                    >
                      {isPayout ? (
                        <ArrowUp className="h-4 w-4" />
                      ) : (
                        <ArrowDown className="h-4 w-4" />
                      )}
                      <span>{isPayout ? "Pagar" : "Receber"}</span>
                    </div>
                  </TableCell>
                  <TableCell>{category || "-"}</TableCell>
                  <TableCell
                    className={cn(
                      "font-semibold",
                      isPayout ? "text-red-600" : "text-green-600"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {Mask.formatCurrency(value)}
                      {!isReconciled && (
                        <Badge variant="outline" className="text-amber-600">
                          Pendente
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {formatPaymentDate(item.payment_date)}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="h-24 text-center">
                Nenhum registro encontrado no período.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>

    {dataApi.last_page > 1 && (
      <div className="flex justify-between items-center mb-3">
        <span className="font-medium">
          Exibindo {dataApi.current_page} de um total de {dataApi.last_page}
        </span>
        <Pagination className="w-auto mx-0">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                className={cn(
                  "h-8 bg-blue-600 hover:bg-blue-700",
                  dataApi.current_page <= 1 && "pointer-events-none opacity-50"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(dataApi.current_page - 1);
                }}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                className={cn(
                  "h-8 bg-blue-600 hover:bg-blue-700",
                  dataApi.current_page >= dataApi.last_page &&
                    "pointer-events-none opacity-50"
                )}
                onClick={(e) => {
                  e.preventDefault();
                  onPageChange(dataApi.current_page + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    )}
  </>
);
